import type { FieldProp } from 'react-typed-form';
import type { UploadsController } from '../use-uploads';

import { UploadIcon } from '@phosphor-icons/react';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';

import { ErrorList, Stack, Text } from '../../../components';
import { Color, Style } from '../../../constants';
import useUploads from '../use-uploads';
import UploadCard from './UploadCard';

type Props = Readonly<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  field: FieldProp<any>;
  // Filled in with the controller, for adding files from outside the field.
  controllerRef?: React.RefObject<UploadsController | null>;
}>;

export default function FieldUploads({ field, controllerRef }: Props) {
  const controller = useUploads(field);
  const { addFiles, errorList, isBusy, isLoading, removeUpload, uploads } =
    controller;

  React.useEffect(() => {
    if (controllerRef) controllerRef.current = controller;
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: addFiles,
    disabled: isLoading || isBusy,
  });

  return (
    <Stack gap={1.5}>
      <Stack gap={0.5}>
        <div
          {...getRootProps()}
          {...css.props(
            styles.zone,
            isDragActive && styles.zoneDragActive,
            isBusy && styles.zoneBusy,
            isLoading && styles.zoneLoading
          )}
        >
          <input {...getInputProps()} />
          <Stack style={Style.alignCenter} gap={0.75}>
            <UploadIcon
              size={20}
              color={isDragActive ? Color.blue : Color.neutral60}
            />
            {isDragActive ? (
              <Text>Drop the files here...</Text>
            ) : isBusy ? (
              <Text>Uploading...</Text>
            ) : (
              <Text>
                Drag and Drop some files here or{' '}
                <Text typeface="body.medium" color="accent">
                  Choose files
                </Text>
              </Text>
            )}
          </Stack>
        </div>
        <ErrorList errors={errorList} />
      </Stack>
      {uploads.length > 0 && (
        <html.div style={styles.list}>
          {uploads.map((upload, i) => {
            const originalName = upload.file.name;
            const extension = originalName.split('.').pop() ?? '';
            const isImage = [
              'gif',
              'jpg',
              'jpeg',
              'png',
              'svg',
              'webp',
            ].includes(extension.toLowerCase());

            return (
              <UploadCard
                key={`${originalName}${i}`}
                inProgress={!upload.iri}
                originalName={originalName}
                thumbUrl={
                  isImage ? URL.createObjectURL(upload.file) : undefined
                }
                onDelete={
                  !isBusy && upload.iri ? () => removeUpload(i) : undefined
                }
              />
            );
          })}
        </html.div>
      )}
    </Stack>
  );
}

const styles = css.create({
  zone: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    borderWidth: 1.5,
    borderRadius: 8,
    borderColor: color.neutral20,
    borderStyle: 'dashed',
    outline: 'none',
    transition: 'background-color 0.15s',
    cursor: 'pointer',
    overflow: 'clip',
    ':hover': {
      backgroundColor: color.neutral10,
    },
  },
  zoneDragActive: {
    backgroundColor: color.neutral10,
  },
  zoneBusy: {
    cursor: 'default',
  },
  zoneLoading: {
    cursor: 'default',
    backgroundColor: color.neutral10,
    borderColor: color.neutral20,
  },
  list: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: 10,
  },
});
